import { Select, Tabs } from "@omnitok/ui";
import { Disclosure } from "@pervasivemind/admin-ui";
import {
  ArrowRight,
  BookOpen,
  HelpCircle,
  Lightbulb,
  MessageSquare,
  Sparkles,
  Trash2,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "@/lib/api";
import { adminIssuesApi } from "@/lib/admin-ai-api";
import { duckdbApi } from "@/lib/builder-api";
import { resolvePrimaryTable } from "@/lib/primary-table";
import { trainingApi } from "@/lib/training-api";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n";
import { ChipsInput, Field } from "@/pages/builder/components/Field";
import { BilingualInput } from "@/pages/builder/components/BilingualInput";
import { DocsEditor } from "@/pages/builder/ai/DocsEditor";
import { ExamplesEditor } from "@/pages/builder/ai/ExamplesEditor";
import { InsightSpecsEditor } from "@/pages/builder/ai/InsightSpecsEditor";
import { AiHomeReferenceSection } from "@/pages/builder/ai/AiHomeReferenceSection";
import { AiHomeTimeSection } from "@/pages/builder/ai/AiHomeTimeSection";
import { PromptOverrideEditor } from "@/pages/builder/ai/PromptOverrideEditor";
import { ExecutionsTable } from "@/pages/builder/ai/ExecutionsTable";
import { SnapshotsPanel } from "@/pages/builder/ai/SnapshotsPanel";
import { IssuesQueue } from "@/pages/panorama/IssuesQueue";
import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";
import type {
  CTACatalogItem,
  DisplayLabel,
  DuckDBColumnInfo,
  InsightsDataConfig,
  InsightsGranularity,
  LocalizedString,
} from "@/types";

interface AITabProps {
  draft: UseBuilderDraft;
}

interface LlmModelOption {
  id: string;
  label: string;
}

interface LlmModelCatalog {
  /** The customer's effective catalog (platform + its exceptions). */
  models: LlmModelOption[];
  /** What each function uses with no report override (chat, analysis, …). */
  defaults: Record<string, string>;
  /** Why the catalog could not be loaded; null when it loaded. */
  error: string | null;
}

/**
 * Shared hook: fetch the customer's model catalog exposed by
 * ``GET /api/builder/llm/models``. Both the **Insights / AI Analysis**
 * dropdown and the **Chat** dropdown read the same list. The catalog and
 * the defaults are configured in the platform console (Modelos IA) and
 * reach this screen without a redeploy.
 */
function useLlmModelCatalog(): LlmModelCatalog {
  const [catalog, setCatalog] = useState<LlmModelCatalog>({
    models: [],
    defaults: {},
    error: null,
  });
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const data = await api.get<Omit<LlmModelCatalog, "error">>(
          "/api/builder/llm/models",
        );
        if (cancelled) return;
        setCatalog({
          models: data.models,
          defaults: data.defaults,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setCatalog((c) => ({ ...c, error: err instanceof Error ? err.message : String(err) }));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);
  return catalog;
}

/**
 * Selector options: "default (X)" first, then the catalog. A stored value
 * that left the catalog stays visible (marked) so the admin sees why
 * publishing refuses it, instead of the select silently showing another.
 */
function modelOptions(
  catalog: LlmModelCatalog,
  role: string,
  current: string,
  t: (key: string, vars?: Record<string, string>) => string,
): Array<{ value: string; label: string }> {
  const fallback = catalog.defaults[role];
  const options = [
    {
      value: "",
      label: fallback
        ? t("editor.ai.model.useDefaultNamed", { model: fallback })
        : t("editor.ai.model.useDefault"),
    },
    ...catalog.models.map((m) => ({ value: m.id, label: `${m.label} (${m.id})` })),
  ];
  if (current && catalog.error === null && !catalog.models.some((m) => m.id === current)) {
    options.push({
      value: current,
      label: t("editor.ai.model.outsideCatalog", { model: current }),
    });
  }
  return options;
}

// ── PR-2 builder reorg: la IA del reporte repensada ─────────────────
//
// La navegación anterior anidaba 3 niveles de tabs (AI →
// General/Chat/Insights → Docs/Examples/Prompt) — el prompt override
// quedaba a 3 clicks invisibles. El rediseño (spec congelada con
// Pablo, 2026-07-16) la reemplaza por:
//
//   Resumen (salud + 3 cards) · Conocimiento · Chat · Insights
//
// Un solo nivel de pills; lo técnico (modelos, prompt override, modo
// semanal legacy) queda plegado en <details> "Avanzado". Los editores
// existentes (DocsEditor, ExamplesEditor, InsightSpecsEditor,
// PromptOverrideEditor, ExecutionsTable) se reutilizan sin cambios.
// PR-4 sumó el pill "Issues" (cola report-scoped, badge con el open
// count) y el bloque de Snapshots en el paso ③ de Insights.

type AIPane = "overview" | "knowledge" | "chat" | "insights" | "issues";

const PANES: ReadonlyArray<{ key: AIPane; i18nKey: string }> = [
  { key: "overview", i18nKey: "editor.ai.panes.overview" },
  { key: "knowledge", i18nKey: "editor.ai.panes.knowledge" },
  { key: "chat", i18nKey: "editor.ai.panes.chat" },
  { key: "insights", i18nKey: "editor.ai.panes.insights" },
  { key: "issues", i18nKey: "editor.ai.panes.issues" },
];

interface TrainingCounts {
  docs: number;
  examples: number;
}

/**
 * Doc/example counts for the health card. ``null`` = not loaded or the
 * fetch failed — the card shows "—" instead of a misleading 0 (the
 * panes load their own data and surface real errors there).
 */
function useTrainingCounts(draftKey: string): TrainingCounts | null {
  const [counts, setCounts] = useState<TrainingCounts | null>(null);
  useEffect(() => {
    let cancelled = false;
    setCounts(null);
    void (async () => {
      try {
        const res = await trainingApi.list(draftKey);
        if (cancelled) return;
        setCounts({
          docs: res.items.filter((i) => i.training_type === "documentation")
            .length,
          examples: res.items.filter((i) => i.training_type === "example")
            .length,
        });
      } catch {
        if (!cancelled) setCounts(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draftKey]);
  return counts;
}

/**
 * Open (new + in_review) issue count for this report's badge. Two
 * cheap `page_size=1` list calls summed — the metrics endpoint is
 * global-only. `null` = not loaded / fetch failed (badge simply
 * hidden; the Issues pane surfaces the real error).
 */
function useReportOpenIssueCount(
  reportKey: string,
  version: number,
  enabled: boolean,
): number | null {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    if (!enabled) {
      setCount(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [fresh, inReview] = await Promise.all([
          adminIssuesApi.list({
            report_key: reportKey,
            status: "new",
            page_size: 1,
          }),
          adminIssuesApi.list({
            report_key: reportKey,
            status: "in_review",
            page_size: 1,
          }),
        ]);
        if (!cancelled) setCount(fresh.total + inReview.total);
      } catch {
        if (!cancelled) setCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportKey, version, enabled]);
  return count;
}

export function AITab({ draft }: AITabProps): JSX.Element {
  const { t } = useT();
  const { user } = useAuth();
  const [pane, setPane] = useState<AIPane>("overview");
  // The issue-triage endpoints are admin-only — same gate as the
  // hub's Accesos / IA del proyecto tabs.
  const isAdmin = user?.role === "admin";
  // Bumped after any triage action so the badge re-counts.
  const [issuesVersion, setIssuesVersion] = useState(0);
  const openIssueCount = useReportOpenIssueCount(
    draft.key,
    issuesVersion,
    isAdmin,
  );

  const visiblePanes = isAdmin
    ? PANES
    : PANES.filter((p) => p.key !== "issues");

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
      {/* One tab style in the product, not three.
       *
       * This strip used to be PILLS — a filled brand-coloured capsule for
       * the active pane — sitting directly under the page's own underline
       * tabs (Dashboard / Datos / IA). Two tab vocabularies stacked on one
       * screen, and the pill appeared nowhere else in either app (2026-09
       * audit, finding B15). It is now the same underline `Tabs` the
       * project hub, Panorama and Usuarios use, with the open-issue count
       * as its native badge.
       */}
      <div className="mb-6">
        <Tabs
          items={visiblePanes.map((p) => ({
            id: p.key,
            label: t(p.i18nKey),
            badge:
              p.key === "issues" &&
              openIssueCount !== null &&
              openIssueCount > 0
                ? openIssueCount
                : undefined,
          }))}
          activeId={pane}
          onChange={(id: string) => setPane(id as AIPane)}
          variant="underline"
          size="sm"
          aria-label={t("editor.ai.tabs.ariaLabel")}
        />
      </div>

      <div className="min-w-0 space-y-5">
        {pane === "overview" && <OverviewPane draft={draft} onOpen={setPane} />}
        {pane === "knowledge" && <KnowledgePane draft={draft} />}
        {pane === "chat" && <ChatPane draft={draft} />}
        {pane === "insights" && <InsightsPane draft={draft} />}
        {pane === "issues" && isAdmin && (
          <IssuesQueue
            fixedReportKey={draft.key}
            onChanged={() => setIssuesVersion((v) => v + 1)}
          />
        )}
      </div>
    </div>
  );
}

// ── Pane: Resumen (salud) ───────────────────────────────────

interface OverviewPaneProps extends AITabProps {
  onOpen: (pane: AIPane) => void;
}

function OverviewPane({ draft, onOpen }: OverviewPaneProps): JSX.Element {
  const { t } = useT();
  const ai = draft.ai;
  const counts = useTrainingCounts(draft.key);

  const questionCount = (
    (draft.report.proposed_questions as DisplayLabel[] | undefined) ?? []
  ).length;
  const ctaCount = (
    (draft.report.cta_catalog as CTACatalogItem[] | undefined) ?? []
  ).length;
  const specCount = (ai.insight_specs ?? []).length;
  const hasPromptOverride = Boolean(
    (ai.prompt_override ?? "").toString().trim(),
  );
  const chatModel =
    ((draft.report.chat_model as string | undefined) ?? "").trim();
  const temporalMode = ai.insights_data_config != null;

  const fmt = (n: number | undefined): string =>
    n === undefined ? "—" : String(n);

  return (
    <>
      {/* Salud: el switch global primero */}
      <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card flex flex-wrap items-center gap-4 p-5">
        <label className="flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            checked={ai.enabled !== false}
            onChange={(e) => draft.setAI({ enabled: e.target.checked })}
            className="h-5 w-5 rounded border-neutral-300 text-primary-dark focus:ring-primary"
          />
          <span>
            <span className="block text-sm font-semibold text-neutral-900">
              {t("editor.ai.overview.enabledTitle")}
            </span>
            <span className="block text-xs text-neutral-500">
              {t("editor.ai.overview.enabledHint")}
            </span>
          </span>
        </label>
      </section>

      <AiHomeTimeSection draft={draft} />
      <AiHomeReferenceSection draft={draft} />

      {/* Las 3 piezas */}
      <div className="grid gap-4 md:grid-cols-3">
        <OverviewCard
          icon={<BookOpen className="h-4 w-4" />}
          title={t("editor.ai.panes.knowledge")}
          description={t("editor.ai.overview.knowledgeDesc")}
          lines={[
            t("editor.ai.overview.knowledgeCounts", {
              docs: fmt(counts?.docs),
              examples: fmt(counts?.examples),
            }),
            t("editor.ai.overview.focusCount", {
              count: (ai.focus_areas ?? []).length,
            }),
            hasPromptOverride
              ? t("editor.ai.overview.promptOverrideOn")
              : t("editor.ai.overview.promptOverrideOff"),
          ]}
          onOpen={() => onOpen("knowledge")}
        />
        <OverviewCard
          icon={<MessageSquare className="h-4 w-4" />}
          title={t("editor.ai.panes.chat")}
          description={t("editor.ai.overview.chatDesc")}
          lines={[
            chatModel
              ? t("editor.ai.overview.modelOverride", { model: chatModel })
              : t("editor.ai.overview.modelDefault"),
            t("editor.ai.overview.chatCounts", {
              questions: questionCount,
              ctas: ctaCount,
            }),
          ]}
          onOpen={() => onOpen("chat")}
        />
        <OverviewCard
          icon={<Lightbulb className="h-4 w-4" />}
          title={t("editor.ai.panes.insights")}
          description={t("editor.ai.overview.insightsDesc")}
          lines={[
            t("editor.ai.overview.specCount", { count: specCount }),
            temporalMode
              ? t("editor.ai.overview.temporalMode")
              : t("editor.ai.overview.weeklyMode"),
            t("editor.ai.overview.maxInsights", {
              count: ai.max_insights ?? 3,
            }),
          ]}
          onOpen={() => onOpen("insights")}
        />
      </div>

      <p className="text-xs text-neutral-500">
        {t("editor.ai.overview.contextNotesHint")}
      </p>
    </>
  );
}

interface OverviewCardProps {
  icon: JSX.Element;
  title: string;
  description: string;
  lines: string[];
  onOpen: () => void;
}

function OverviewCard({
  icon,
  title,
  description,
  lines,
  onOpen,
}: OverviewCardProps): JSX.Element {
  const { t } = useT();
  return (
    <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card flex flex-col p-5">
      <header className="mb-2 flex items-center gap-2 text-primary-dark">
        {icon}
        <h2 className="text-sm font-semibold text-neutral-900">{title}</h2>
      </header>
      <p className="mb-3 text-xs leading-relaxed text-neutral-500">
        {description}
      </p>
      <ul className="mb-4 space-y-1">
        {lines.map((line, i) => (
          <li key={i} className="text-xs text-neutral-600">
            • {line}
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onOpen}
        className="mt-auto inline-flex w-fit items-center gap-1 rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:border-primary/50 hover:text-primary-ink"
      >
        {t("editor.ai.overview.open")}
        <ArrowRight size={12} />
      </button>
    </section>
  );
}

// ── Bloque "Avanzado" plegable ──────────────────────────────

function AdvancedBlock({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const { t } = useT();
  return (
    <Disclosure
      summary={t("editor.ai.advanced")}
      summaryClassName="px-5 py-3.5"
      className="shadow-sm"
    >
      <div className="space-y-5 px-2 pb-2">{children}</div>
    </Disclosure>
  );
}

// ── Pane: Conocimiento ──────────────────────────────────────

/**
 * Lo que la IA sabe del reporte — alimenta al chat Y a los insights.
 * Focus areas + docs + ejemplos a página completa (las listas escalan);
 * el prompt override (Jinja2) queda en Avanzado.
 */
function KnowledgePane({ draft }: AITabProps): JSX.Element {
  const { t } = useT();
  const ai = draft.ai;

  return (
    <>
      <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5">
        <header className="mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary-dark" />
          <h2 className="text-base font-semibold text-neutral-900">
            {t("editor.ai.focusAreasLabel")}
          </h2>
        </header>
        {/* The Field label used to repeat the card title 20 px above it,
            word for word. When a label would say what the heading just
            said, it is dropped, not shrunk (design language §1). */}
        <Field hint={t("editor.ai.focusAreasHint")}>
          <ChipsInput
            value={ai.focus_areas ?? []}
            onChange={(next) => draft.setAI({ focus_areas: next })}
            placeholder={t("editor.ai.focusAreasPlaceholder")}
          />
        </Field>
      </section>

      <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5">
        <header className="mb-3 flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary-dark" />
          <h2 className="text-base font-semibold text-neutral-900">
            {t("editor.ai.subtabs.docs")}
          </h2>
        </header>
        <DocsEditor draftKey={draft.key} />
      </section>

      <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5">
        <header className="mb-3 flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-primary-dark" />
          <h2 className="text-base font-semibold text-neutral-900">
            {t("editor.ai.subtabs.examples")}
          </h2>
        </header>
        <ExamplesEditor draftKey={draft.key} />
      </section>

      <AdvancedBlock>
        <PromptOverrideEditor draft={draft} />
      </AdvancedBlock>
    </>
  );
}

// ── Pane: Chat ──────────────────────────────────────────────

/**
 * Lo que ve el cliente en el chat: preguntas de arranque + CTAs.
 * El override de modelo queda en Avanzado.
 */
function ChatPane({ draft }: AITabProps): JSX.Element {
  return (
    <>
      <StarterQuestionsSection draft={draft} />
      <CtaCatalogSection draft={draft} />
      <AdvancedBlock>
        <ChatModelSection draft={draft} />
      </AdvancedBlock>
    </>
  );
}

// ── Pane: Insights (3 pasos) ────────────────────────────────

/**
 * El AI Analysis en 3 pasos: ① qué analiza (specs) → ② cómo y cuándo
 * (ventana temporal) → ③ resultados (snapshots persistidos con
 * watcher/backfill/regenerate — PR-4 — más la tabla de ejecuciones).
 * El modelo queda en Avanzado.
 */
function InsightsPane({ draft }: AITabProps): JSX.Element {
  const { t } = useT();
  const { user } = useAuth();
  // The snapshot admin endpoints (list / regenerate / backfill /
  // trigger) are admin-only — non-admins keep the executions table.
  const isAdmin = user?.role === "admin";

  return (
    <>
      <BlockHeader n={1} title={t("editor.ai.blocks.what")} />
      <InsightSpecsEditor draft={draft} />

      <BlockHeader n={2} title={t("editor.ai.blocks.when")} />
      <SnapshotsConfigSection draft={draft} />
      <AdvancedBlock>
        <InsightsModelField draft={draft} />
      </AdvancedBlock>

      <BlockHeader n={3} title={t("editor.ai.blocks.results")} />
      {isAdmin && <SnapshotsPanel draftKey={draft.key} />}
      <ExecutionsTable draftKey={draft.key} />
    </>
  );
}

function BlockHeader({ n, title }: { n: number; title: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 pt-1">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-dark text-xs font-bold text-white">
        {n}
      </span>
      <h2 className="text-sm font-semibold text-neutral-900">{title}</h2>
    </div>
  );
}

/** Selector del modelo de insights (antes una card propia). */
function InsightsModelField({ draft }: AITabProps): JSX.Element {
  const { t } = useT();
  const ai = draft.ai;
  const catalog = useLlmModelCatalog();

  return (
    <Field label={t("editor.ai.modelLabel")}>
      {catalog.error !== null && (
        <p role="alert" className="mb-1 text-xs text-error-dark">
          {t("editor.ai.modelCatalogError", { err: catalog.error })}
        </p>
      )}
      <Select
        options={modelOptions(catalog, "analysis", ai.model ?? "", t)}
        value={ai.model ?? ""}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          draft.setAI({ model: e.target.value || undefined })
        }
        fullWidth
      />
    </Field>
  );
}

/**
 * Granularity options — single source of truth for the selector. The two
 * quarter variants are deliberately distinct: ``quarter`` is the calendar
 * quarter, ``fiscal_quarter`` is the HP fiscal quarter (FY runs Nov→Oct).
 */
const GRANULARITY_OPTIONS: ReadonlyArray<{
  value: InsightsGranularity;
  i18nKey: string;
}> = [
  { value: "day", i18nKey: "editor.ai.snapshots.grain.day" },
  { value: "week", i18nKey: "editor.ai.snapshots.grain.week" },
  { value: "month", i18nKey: "editor.ai.snapshots.grain.month" },
  { value: "quarter", i18nKey: "editor.ai.snapshots.grain.quarter" },
  { value: "fiscal_quarter", i18nKey: "editor.ai.snapshots.grain.fiscalQuarter" },
];

/** A DuckDB type string is "temporal" if it mentions date/time. */
function isTemporalType(duckType: string): boolean {
  return /date|time/i.test(duckType);
}

/**
 * Settings exposed to the builder admin for the persisted-snapshot
 * workflow. Two mutually-exclusive modes:
 *
 * - **Legacy weekly** (``ai_config.insights_data_config`` absent) —
 *   ``week_column`` + ``prior_offsets_weeks``; the portal anchors snapshots
 *   to the ISO week, byte-for-byte the historical behaviour. A CTA seeds
 *   the generalized config from these values.
 * - **Per-report temporal** (``insights_data_config`` present) —
 *   ``{date_column, granularity, data_lookback_periods,
 *   analysis_lookback_periods}``. The date column is picked from a dropdown
 *   populated by DuckDB introspection (temporal columns first). Reverting
 *   clears the sub-object → back to weekly.
 *
 * ``max_insights`` applies to both modes, so it always renders.
 */
function SnapshotsConfigSection({ draft }: AITabProps): JSX.Element {
  const { t } = useT();
  const ai = draft.ai;
  const cfg = ai.insights_data_config;
  const temporalEnabled = cfg != null;

  // The same pair publish validates: the table the ENGINE queries (a
  // declared source wins over the legacy `duckdb_table` field, which the
  // create modal used to fill with the report key) and the slug the
  // server resolved for this draft. The slug used to come from a client-side
  // lookup that starts as null, so the first request went out without
  // it — and the builder refuses that ("preview needs a project_slug").
  const projectSlug = draft.draft?.project_slug;
  const table = resolvePrimaryTable(draft.report);

  // Introspect columns for the date-column dropdown — only in temporal
  // mode (legacy reports never open it) and against the PER-PROJECT DuckDB
  // (projectSlug), so gating-style materialized views resolve correctly.
  const [columns, setColumns] = useState<DuckDBColumnInfo[]>([]);
  const [colsLoading, setColsLoading] = useState(false);
  const [colsError, setColsError] = useState<string | null>(null);
  useEffect(() => {
    if (!temporalEnabled || !table) {
      setColumns([]);
      return;
    }
    let cancelled = false;
    setColsLoading(true);
    setColsError(null);
    void (async () => {
      try {
        const cols = await duckdbApi.columns(table, projectSlug);
        if (!cancelled) setColumns(cols);
      } catch (err) {
        if (!cancelled) {
          setColsError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setColsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [temporalEnabled, table, projectSlug]);

  const offsetsValue = (ai.prior_offsets_weeks ?? []).map(String);

  function parseOffsets(values: string[]): number[] {
    const seen = new Set<number>();
    const out: number[] = [];
    for (const raw of values) {
      const n = Number.parseInt(raw, 10);
      if (Number.isFinite(n) && n > 0 && !seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    }
    return out;
  }

  function patchTemporal(patch: Partial<InsightsDataConfig>): void {
    if (!cfg) return;
    draft.setAI({ insights_data_config: { ...cfg, ...patch } });
  }

  function enableTemporal(): void {
    // Seed from the legacy values so the switch is loss-free: the week
    // column becomes the date column, granularity starts at "week", and
    // the prior-offset count seeds the analysis lookback.
    const offsets = ai.prior_offsets_weeks ?? [];
    draft.setAI({
      insights_data_config: {
        date_column: (ai.week_column ?? "").trim() || "week_iso",
        granularity: "week",
        data_lookback_periods: 12,
        analysis_lookback_periods: offsets.length > 0 ? offsets.length : 3,
      },
    });
  }

  function disableTemporal(): void {
    // Undefined → dropped on JSON serialization → absent → legacy weekly.
    draft.setAI({ insights_data_config: undefined });
  }

  // Date-column dropdown: temporal columns first, then alpha. The current
  // value always appears even if introspection hasn't loaded it yet (or it
  // no longer exists), so the selection round-trips without silent loss.
  const columnNames = columns.map((c) => c.name);
  const sortedColumns = [...columns].sort((a, b) => {
    const rank = (isTemporalType(a.type) ? 0 : 1) - (isTemporalType(b.type) ? 0 : 1);
    return rank !== 0 ? rank : a.name.localeCompare(b.name);
  });
  const currentColMissing =
    cfg != null && cfg.date_column !== "" && !columnNames.includes(cfg.date_column);

  return (
    <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5">
      <header className="mb-4 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary-dark" />
        <h2 className="text-base font-semibold text-neutral-900">
          {t("editor.ai.snapshots.title")}
        </h2>
      </header>
      <p className="mb-4 text-xs text-neutral-500">
        {t("editor.ai.snapshots.description")}
      </p>

      <div className="space-y-4">
        <Field
          label={t("editor.ai.snapshots.maxInsightsLabel")}
          hint={t("editor.ai.snapshots.maxInsightsHint")}
        >
          <input
            type="number"
            min={1}
            max={6}
            value={ai.max_insights ?? 3}
            onChange={(e) => {
              const n = Number.parseInt(e.target.value, 10);
              if (Number.isFinite(n)) {
                draft.setAI({ max_insights: Math.max(1, Math.min(6, n)) });
              }
            }}
            className="block rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-24"
          />
        </Field>

        {!temporalEnabled ? (
          <>
            <Field
              label={t("editor.ai.snapshots.weekColumnLabel")}
              hint={t("editor.ai.snapshots.weekColumnHint")}
            >
              <input
                type="text"
                value={ai.week_column ?? ""}
                onChange={(e) => draft.setAI({ week_column: e.target.value })}
                placeholder="week_iso"
                className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary font-mono"
              />
            </Field>

            <Field
              label={t("editor.ai.snapshots.priorOffsetsLabel")}
              hint={t("editor.ai.snapshots.priorOffsetsHint")}
            >
              <ChipsInput
                value={offsetsValue}
                onChange={(next) =>
                  draft.setAI({ prior_offsets_weeks: parseOffsets(next) })
                }
                placeholder="1, 2, 4, 8, 13, 26, 52"
              />
            </Field>

            <div className="rounded-md border border-dashed border-primary/50 bg-primary-soft/50 p-3">
              <p className="mb-2 text-xs leading-relaxed text-neutral-600">
                {t("editor.ai.snapshots.temporalCtaDescription")}
              </p>
              <button
                type="button"
                onClick={enableTemporal}
                className="inline-flex items-center gap-1 rounded-md border border-primary-light px-3 py-1.5 text-sm font-medium text-primary-ink hover:bg-primary-line"
              >
                {t("editor.ai.snapshots.switchToTemporal")}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary-soft px-3 py-1.5 text-xs font-medium text-primary-ink">
              <Lightbulb className="h-3.5 w-3.5" />
              {t("editor.ai.snapshots.temporalActiveBadge")}
            </div>

            <Field
              label={t("editor.ai.snapshots.dateColumnLabel")}
              hint={t("editor.ai.snapshots.dateColumnHint")}
            >
              {colsLoading && (
                <p className="mb-1 text-xs text-neutral-500">
                  {t("editor.ai.snapshots.columnsLoading")}
                </p>
              )}
              {colsError && (
                <p className="mb-1 text-xs text-error-dark">
                  {t("editor.ai.snapshots.columnsError")}
                </p>
              )}
              <Select
                // Two guards from the old JSX survive as conditional
                // spreads: the empty entry only while nothing is chosen,
                // and a synthetic entry for a column the saved config
                // names but the table no longer has. Without the second
                // one the select would silently re-point the report at
                // whichever column happens to sort first.
                options={[
                  ...(cfg.date_column === ""
                    ? [{ value: "", label: "—" }]
                    : []),
                  ...(currentColMissing
                    ? [{ value: cfg.date_column, label: cfg.date_column }]
                    : []),
                  ...sortedColumns.map((c) => ({
                    value: c.name,
                    label: isTemporalType(c.type)
                      ? c.name
                      : `${c.name} (${t("editor.ai.snapshots.nonTemporalTag")})`,
                  })),
                ]}
                value={cfg.date_column}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  patchTemporal({ date_column: e.target.value })
                }
                className="font-mono"
                fullWidth
              />
            </Field>

            <Field
              label={t("editor.ai.snapshots.granularityLabel")}
              hint={t("editor.ai.snapshots.granularityHint")}
            >
              <Select
                options={GRANULARITY_OPTIONS.map((g) => ({
                  value: g.value,
                  label: t(g.i18nKey),
                }))}
                value={cfg.granularity}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  patchTemporal({
                    granularity: e.target.value as InsightsGranularity,
                  })
                }
                fullWidth
              />
            </Field>

            <Field
              label={t("editor.ai.snapshots.dataLookbackLabel")}
              hint={t("editor.ai.snapshots.dataLookbackHint")}
            >
              <input
                type="number"
                min={1}
                value={cfg.data_lookback_periods}
                onChange={(e) => {
                  const n = Number.parseInt(e.target.value, 10);
                  if (Number.isFinite(n)) {
                    patchTemporal({ data_lookback_periods: Math.max(1, n) });
                  }
                }}
                className="block rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-24"
              />
            </Field>

            <Field
              label={t("editor.ai.snapshots.analysisLookbackLabel")}
              hint={t("editor.ai.snapshots.analysisLookbackHint")}
            >
              <input
                type="number"
                min={0}
                value={cfg.analysis_lookback_periods}
                onChange={(e) => {
                  const n = Number.parseInt(e.target.value, 10);
                  if (Number.isFinite(n)) {
                    patchTemporal({ analysis_lookback_periods: Math.max(0, n) });
                  }
                }}
                className="block rounded-[10px] border border-control bg-white px-3 py-2 text-sm placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-24"
              />
            </Field>

            <button
              type="button"
              onClick={disableTemporal}
              className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-error-dark"
            >
              {t("editor.ai.snapshots.revertToWeekly")}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Per-report OpenAI model selector for the InsightAgent chat loop.
 * Persists to ``report_config.chat_model``. An empty value follows the
 * customer's ``chat`` model (console: Modelos IA). The catalog is fetched
 * live from ``GET /api/builder/llm/models``.
 */
function ChatModelSection({ draft }: AITabProps): JSX.Element {
  const { t } = useT();
  const catalog = useLlmModelCatalog();
  const catalogError = catalog.error;
  const current = (draft.report.chat_model as string | undefined) ?? "";

  return (
    <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5">
      <header className="mb-4 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-primary-dark" />
        <h2 className="text-base font-semibold text-neutral-900">
          {t("editor.ai.chatModel.title")}
        </h2>
      </header>
      <p className="mb-4 text-xs text-neutral-500">
        {t("editor.ai.chatModel.description")}
      </p>

      <Field
        label={t("editor.ai.chatModel.label")}
        hint={t("editor.ai.chatModel.hint")}
      >
        {catalogError !== null && (
          <p role="alert" className="mb-1 text-xs text-error-dark">
            {t("editor.ai.modelCatalogError", { err: catalogError })}
          </p>
        )}
        <Select
          options={modelOptions(catalog, "chat", current, t)}
          value={current}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            draft.setReport({ chat_model: e.target.value || undefined })
          }
          fullWidth
        />
      </Field>
    </section>
  );
}

/**
 * Editor for ``report_config.proposed_questions`` — the curated list
 * of starter questions shown in the chat sidebar's empty state. When
 * non-empty, the portal returns these verbatim instead of falling
 * back to the frontend's generic defaults.
 */
function StarterQuestionsSection({ draft }: AITabProps): JSX.Element {
  const { t } = useT();
  // ``proposed_questions`` is ``Array<DisplayLabel>`` post-iter 16 —
  // each item is either a legacy plain ``string`` or a bilingual
  // ``{en, es}`` object. Earlier this editor rendered ``value={q}``
  // directly into a single ``<input>``, which made React stringify
  // the object as ``"[object Object]"`` whenever the data was already
  // bilingual. Routing each item through ``BilingualInput`` fixes the
  // bug and gives the admin a real EN+ES side-by-side editor.
  const items = (draft.report.proposed_questions as DisplayLabel[] | undefined) ?? [];

  function update(next: DisplayLabel[]): void {
    draft.setReport({ proposed_questions: next });
  }

  function add(): void {
    // Start new rows as a bilingual ``{en, es}`` so future-locale
    // additions don't have to migrate the on-disk shape.
    update([...items, { en: "", es: "" }]);
  }

  function patch(idx: number, value: LocalizedString): void {
    update(items.map((it, i) => (i === idx ? value : it)));
  }

  function remove(idx: number): void {
    update(items.filter((_, i) => i !== idx));
  }

  return (
    <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5">
      <header className="mb-4 flex items-center gap-2">
        <HelpCircle className="h-4 w-4 text-primary-dark" />
        <h2 className="text-base font-semibold text-neutral-900">
          {t("editor.ai.starterQuestions.title")}
        </h2>
      </header>
      <p className="mb-4 text-xs text-neutral-500">
        {t("editor.ai.starterQuestions.description")}
      </p>

      <div className="space-y-3">
        {items.length === 0 && (
          <p className="text-sm italic text-neutral-500">
            {t("editor.ai.starterQuestions.empty")}
          </p>
        )}
        {items.map((q, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <BilingualInput
                value={q}
                onChange={(next) => patch(idx, next)}
                placeholder={t("editor.ai.starterQuestions.placeholder")}
              />
            </div>
            <button
              type="button"
              onClick={() => remove(idx)}
              className="mt-1 rounded-md p-1.5 text-neutral-500 hover:bg-error/10 hover:text-error-dark"
              title={t("editor.ai.starterQuestions.remove")}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={add}
        disabled={items.length >= 6}
        className="mt-3 inline-flex items-center gap-1 rounded-md border border-dashed border-neutral-300 px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        + {t("editor.ai.starterQuestions.add")}
      </button>
    </section>
  );
}

/**
 * Editor for ``report_config.cta_catalog`` — actionable suggestion
 * templates surfaced as follow-up CTAs in the chat sidebar. Templates
 * may reference ``{seller_name}``, ``{asin}``, ``{sku}``, ``{client}``
 * etc. — the portal interpolates from the last turn's artifacts.
 */
function CtaCatalogSection({ draft }: AITabProps): JSX.Element {
  const { t } = useT();
  const items = (draft.report.cta_catalog as CTACatalogItem[] | undefined) ?? [];

  function update(next: CTACatalogItem[]): void {
    draft.setReport({ cta_catalog: next });
  }

  function add(): void {
    // Start new rows as bilingual ``{en, es}`` so the editor renders
    // both fields immediately — matches the iter 16 ``LocalizedString``
    // convention and avoids the ``[object Object]`` regression that
    // occurred when items were saved as bilingual but read as a plain
    // string into a single ``<input>``.
    update([
      ...items,
      { label_template: { en: "", es: "" }, description: "" },
    ]);
  }

  function patch(idx: number, patch: Partial<CTACatalogItem>): void {
    update(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function remove(idx: number): void {
    update(items.filter((_, i) => i !== idx));
  }

  return (
    <section className="rounded-[14px] border border-neutral-200 bg-white shadow-card p-5">
      <header className="mb-4 flex items-center gap-2">
        <Zap className="h-4 w-4 text-primary-dark" />
        <h2 className="text-base font-semibold text-neutral-900">
          {t("editor.ai.ctaCatalog.title")}
        </h2>
      </header>
      <p className="mb-4 text-xs text-neutral-500">
        {t("editor.ai.ctaCatalog.description")}
      </p>

      <div className="space-y-3">
        {items.length === 0 && (
          <p className="text-sm italic text-neutral-500">
            {t("editor.ai.ctaCatalog.empty")}
          </p>
        )}
        {items.map((item, idx) => (
          <div
            key={idx}
            className="space-y-2 rounded-md border border-neutral-200 bg-neutral-50 p-3"
          >
            {/* Label template ends up surfaced to the end user in the
                chat sidebar (after entity interpolation), so it has to
                be bilingual. ``BilingualInput`` accepts both legacy
                ``string`` and the new ``{en, es}`` shape via its
                ``toLocalized()`` helper, so existing rows render
                correctly without an explicit DB migration. */}
            <Field label={t("editor.ai.ctaCatalog.labelTemplate")}>
              <BilingualInput
                value={item.label_template}
                onChange={(next) => patch(idx, { label_template: next })}
                placeholder={t("editor.ai.ctaCatalog.labelTemplatePlaceholder")}
                className="font-mono text-sm"
              />
            </Field>
            {/* Description is a builder-only internal note — the LLM
                doesn't see it, end users don't see it. Keep it plain
                ``string`` so the editor isn't cluttered with a
                second-language tab no one will ever fill. */}
            <Field label={t("editor.ai.ctaCatalog.descriptionLabel")}>
              <input
                type="text"
                value={typeof item.description === "string" ? item.description : ""}
                onChange={(e) => patch(idx, { description: e.target.value })}
                placeholder={t("editor.ai.ctaCatalog.descriptionPlaceholder")}
                className="block w-full rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </Field>
            <button
              type="button"
              onClick={() => remove(idx)}
              className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-error-dark"
            >
              <Trash2 size={12} />
              {t("editor.ai.ctaCatalog.remove")}
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={add}
        className="mt-3 inline-flex items-center gap-1 rounded-md border border-dashed border-neutral-300 px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-50"
      >
        + {t("editor.ai.ctaCatalog.add")}
      </button>
    </section>
  );
}
